import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { ErrorText, Loading, cx } from '../components/ui';
import { errorMessage } from '../lib/api';
import { dateTime, timeAgo, timecode } from '../lib/format';
import { qk } from '../queries/keys';
import { fetchViewers } from '../queries/videos';

/**
 * Who watched this recording, for whoever owns it.
 *
 * The count on its own answers "did anyone", which is rarely the question:
 * what the owner sent it for is usually one named person seeing it. So every
 * viewer gets a row, with when they watched and how far they got, since a
 * recording opened and abandoned at four seconds is not one that was watched.
 *
 * People who were signed in are named. Everyone else is a row too, because
 * "three people watched it, one to the end" is a different fact from "four
 * views", and the shape of the interest is the whole point of looking. They
 * are not named, and cannot be: a share link is meant to work for whoever
 * holds it, and putting a face to somebody who was never asked for one would
 * be a different product than the one people are linking to.
 *
 * How far somebody got is not what they saw: a drag to the end reaches 100%
 * having played none of it. So each row draws the stretches actually played
 * along a bar the length of the recording, and the figure beside it is the
 * share of the recording those stretches cover. Gaps in the bar are what was
 * skipped.
 */

/** Played this much of it and it counts as watched; the last credits of a take are rarely played. */
const WATCHED_RATIO = 0.9;

export function Viewers({ videoId, durationSec }: { videoId: string; durationSec: number | null }) {
  const query = useQuery({ queryKey: qk.viewers(videoId), queryFn: () => fetchViewers(videoId) });

  if (query.isPending) return <Loading label="Loading who watched" />;
  if (query.isError) {
    return (
      <div className="p-3">
        <ErrorText>{errorMessage(query.error)}</ErrorText>
      </div>
    );
  }

  const { people, anonymous, emails } = query.data;
  // Addresses from the gate that are not already a named account, so the
  // same person is not listed twice under two descriptions.
  const named = new Set(people.map((person) => person.email.toLowerCase()));
  const guests = emails.filter((one) => !named.has(one.email.toLowerCase()));

  if (people.length === 0 && anonymous.length === 0 && guests.length === 0) {
    return (
      <p className="px-3 py-8 text-center text-caption text-chalk-faint">
        Nobody has opened this yet. Everyone who does appears here: by name if they were signed in, and as a row
        either way.
      </p>
    );
  }

  return (
    <div className="space-y-4 p-1">
      <ul className="space-y-2">
        {people.map((person) => (
          <ViewerRow
            key={person.id}
            avatar={
              person.avatarUrl ? (
                <img src={person.avatarUrl} alt="" className="h-7 w-7 shrink-0 rounded-full object-cover" />
              ) : (
                <Monogram>{(person.name?.trim() || person.email).slice(0, 1)}</Monogram>
              )
            }
            name={person.name?.trim() || person.email}
            views={person.views}
            lastSeenAt={person.lastSeenAt}
            maxProgressSec={person.maxProgressSec}
            completed={person.completed}
            playedRanges={person.playedRanges}
            playedSec={person.playedSec}
            durationSec={durationSec}
          />
        ))}
        {anonymous.map((viewer, index) => (
          <ViewerRow
            key={viewer.id}
            avatar={<Monogram dim>?</Monogram>}
            name={anonymous.length > 1 ? `Anonymous viewer ${index + 1}` : 'Anonymous viewer'}
            muted
            views={viewer.views}
            lastSeenAt={viewer.lastSeenAt}
            maxProgressSec={viewer.maxProgressSec}
            completed={viewer.completed}
            playedRanges={viewer.playedRanges}
            playedSec={viewer.playedSec}
            durationSec={durationSec}
          />
        ))}
      </ul>

      {guests.length > 0 ? (
        <div>
          <p className="mb-1.5 text-caption text-chalk-faint">
            Gave an address to watch. Anyone can type any address, so these are not accounts.
          </p>
          <ul className="space-y-1">
            {guests.map((guest) => (
              <li key={guest.email} className="flex items-baseline justify-between gap-3 text-caption">
                <span className="min-w-0 truncate text-chalk-dim">{guest.email}</span>
                <span className="shrink-0 text-chalk-faint">{dateTime(guest.createdAt)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function Monogram({ children, dim = false }: { children: ReactNode; dim?: boolean }) {
  return (
    <span
      className={cx(
        'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-hairline bg-slate-800 text-caption uppercase',
        dim ? 'text-chalk-faint' : 'text-chalk-dim',
      )}
    >
      {children}
    </span>
  );
}

/**
 * One viewer. The same row whoever they are, so the eye can compare them:
 * who, when, and what they saw.
 */
function ViewerRow({
  avatar,
  name,
  muted = false,
  views,
  lastSeenAt,
  maxProgressSec,
  completed,
  playedRanges,
  playedSec,
  durationSec,
}: {
  avatar: ReactNode;
  name: string;
  /** Anonymous rows sit a shade back from the named ones. */
  muted?: boolean;
  views: number;
  lastSeenAt: string;
  maxProgressSec: number;
  completed: boolean;
  playedRanges: Array<[number, number]>;
  playedSec: number;
  durationSec: number | null;
}) {
  const known = durationSec !== null && durationSec > 0;
  // Views from before the player reported what it played have only how
  // far they got; those rows keep the old reading of it.
  const traced = playedRanges.length > 0;
  const percent = known ? Math.min(100, Math.round((maxProgressSec / durationSec) * 100)) : null;
  const playedPercent = known && traced ? Math.min(100, Math.round((playedSec / durationSec) * 100)) : null;
  const watched = traced && known ? playedSec >= durationSec * WATCHED_RATIO : completed;
  // Got to the end without seeing most of it: worth saying in words, since
  // "reached the end" is what the old figure would have called watched.
  const skipped = traced && !watched && (completed || (percent !== null && percent >= 95));

  const figure = watched
    ? 'Watched'
    : playedPercent !== null
      ? `${playedPercent}% played`
      : percent !== null
        ? `${percent}%`
        : timecode(traced ? playedSec : maxProgressSec);
  const detail = traced
    ? `Played ${timecode(playedSec)}${known ? ` of ${timecode(durationSec)}` : ''}, reached ${timecode(maxProgressSec)}${
        skipped ? '. Skipped ahead over the rest.' : ''
      }`
    : `Reached ${timecode(maxProgressSec)}`;

  return (
    <li className="flex items-center gap-2.5 rounded border border-hairline px-2.5 py-2">
      {avatar}
      <span className="min-w-0 flex-1">
        <span className={cx('block truncate text-body', muted ? 'text-chalk-dim' : 'text-chalk')}>{name}</span>
        {/* The date said outright, with how long ago after it: "when" is the
            question, and a relative age alone makes you hover to find out. */}
        <span className="block truncate text-caption text-chalk-faint">
          {dateTime(lastSeenAt)}
          <span className="text-chalk-faint/70"> · {timeAgo(lastSeenAt)}</span>
          {views > 1 ? ` · ${views} times` : ''}
        </span>
      </span>
      <span className="shrink-0 text-right" title={detail}>
        <span className={cx('block text-caption', watched ? 'text-go' : skipped ? 'text-caution' : 'text-chalk-dim')}>
          {figure}
        </span>
        {known && traced ? (
          <PlayedBar ranges={playedRanges} durationSec={durationSec} watched={watched} />
        ) : known && !watched && percent !== null ? (
          <span className="mt-1 block h-1 w-20 overflow-hidden rounded-full bg-slate-800">
            <span className="block h-full rounded-full bg-chalk-dim" style={{ width: `${percent}%` }} />
          </span>
        ) : null}
      </span>
    </li>
  );
}

/**
 * The recording as a bar, with the stretches this viewer played drawn on it.
 * A stretch narrower than a pixel is still drawn a pixel wide: a second
 * glanced at in an hour is a mark, not nothing.
 */
function PlayedBar({
  ranges,
  durationSec,
  watched,
}: {
  ranges: Array<[number, number]>;
  durationSec: number;
  watched: boolean;
}) {
  return (
    <span
      className="relative mt-1 block h-1 w-20 overflow-hidden rounded-full bg-slate-800"
      role="img"
      aria-label={`Played stretches: ${ranges.map(([start, end]) => `${timecode(start)} to ${timecode(end)}`).join(', ')}`}
    >
      {ranges.map(([start, end]) => (
        <span
          key={start}
          className={cx('absolute inset-y-0', watched ? 'bg-go' : 'bg-chalk-dim')}
          style={{
            left: `${Math.min(100, (start / durationSec) * 100)}%`,
            width: `max(1px, ${Math.min(100, ((end - start) / durationSec) * 100)}%)`,
          }}
        />
      ))}
    </span>
  );
}
